import React, { Component } from 'react';
import ApiService from '../services/api';
import { t } from '../i18n';
import LcIcon from '../widget/lc-icon.js';

export default class RatingCard extends Component {
    constructor(props) {
        super(props);
        // 回访时 alreadyRated → 初始即"已评价"状态，展示既有星级、不再索要评分。
        this.state = {
            rating: props.alreadyRated ? (props.ratingValue || 0) : 0,
            hoverRating: 0,
            feedback: '',
            submitted: !!props.alreadyRated,
            submitting: false,
            error: null,
        };
    }

    handleStarClick = (star) => {
        if (this.state.submitted || this.state.submitting) return;
        this.setState({ rating: star });
    }

    handleStarHover = (star) => {
        if (this.state.submitted || this.state.submitting) return;
        this.setState({ hoverRating: star });
    }

    handleStarLeave = () => {
        this.setState({ hoverRating: 0 });
    }

    handleSubmit = async () => {
        const { rating, feedback, submitting, submitted } = this.state;
        const { chatId } = this.props;

        if (submitting || submitted) return;
        if (rating < 1) return;
        if (!chatId) {
            this.setState({ error: t('rating.error_missing_chat') });
            return;
        }

        this.setState({ submitting: true, error: null });

        try {
            const res = await ApiService.rateChat(chatId, rating, feedback);
            // 已评过（后端返回已存在评分 code/msg）也视为已完成状态，避免误导用户重试
            if (res && res.code === 0) {
                this.setState({ submitted: true, submitting: false });
            } else if (res && (res.code === 422 || /已.*评|already/i.test(res.msg || ''))) {
                this.setState({ submitted: true, submitting: false });
            } else {
                this.setState({ error: (res && res.msg) || t('rating.error_submit_failed'), submitting: false });
            }
        } catch (e) {
            this.setState({ error: t('rating.error_network'), submitting: false });
        }
    }

    render() {
        const { theme } = this.props;
        const { rating, hoverRating, feedback, submitted, submitting, error } = this.state;
        const displayRating = hoverRating || rating;

        const styles = {
            container: {
                backgroundColor: 'var(--lc-surface, #ffffff)',
                // 规范 §4.3：禁止 2px solid mainColor 整卡描边 —— 商户配红色主题时
                // 整张卡变红，违反「主色无关」。发丝线 + shadow-1，与其余富卡同款。
                border: 'var(--lc-hairline)',
                borderRadius: 'var(--lc-radius-md)',
                boxShadow: 'var(--lc-shadow-1)',
                padding: 'var(--lc-space-3)',
                margin: 'var(--lc-space-2) 0',
                maxWidth: '320px',
            },
            header: {
                display: 'flex',
                alignItems: 'center',
                marginBottom: 'var(--lc-space-3)',
                color: 'var(--lc-main)',
                fontWeight: 'bold',
                fontSize: '14px',
            },
            starsRow: {
                display: 'flex',
                gap: 'var(--lc-space-2)',
                marginBottom: 'var(--lc-space-3)',
                justifyContent: 'center',
            },
            // 26 = components.card.ratingStarPx。以前是 fontSize:26 撑一个 ★
            // 字形，现在是 <LcIcon size={26}> 的行盒；color 仍然驱动图标本身
            // （LcIcon 画的是 currentColor）。
            star: {
                cursor: submitted ? 'default' : 'pointer',
                transition: 'transform var(--lc-dur-fast), color var(--lc-dur-fast)',
                userSelect: 'none',
                lineHeight: 1,
                display: 'inline-flex',
            },
            textarea: {
                width: '100%',
                minHeight: '60px',
                border: 'var(--lc-hairline)',
                borderRadius: 'var(--lc-radius-xs)',
                padding: 'var(--lc-space-2)',
                fontSize: '13px',
                resize: 'vertical',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
                marginBottom: '10px',
                backgroundColor: 'var(--lc-surface-2, #f1f5f9)',
                color: 'var(--lc-text, #1e293b)',
            },
            button: {
                backgroundColor: 'var(--lc-main)',
                color: 'var(--lc-bubble-user-text, #ffffff)',
                border: 'none',
                borderRadius: 'var(--lc-radius-xs)',
                padding: 'var(--lc-space-2) var(--lc-space-4)',
                cursor: submitting || rating < 1 ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                width: '100%',
                opacity: submitting || rating < 1 ? 0.6 : 1,
                transition: 'opacity var(--lc-dur-base)',
            },
            successMsg: {
                textAlign: 'center',
                color: 'var(--lc-success)',
                fontWeight: 'bold',
                fontSize: '14px',
                padding: 'var(--lc-space-2) 0',
            },
            errorMsg: {
                color: 'var(--lc-danger)',
                fontSize: '12px',
                marginBottom: 'var(--lc-space-2)',
            },
        };

        if (submitted) {
            return (
                <div style={styles.container}>
                    <div style={styles.header}>
                        <LcIcon name="star" size={14} style={{ marginRight: '6px' }} />
                        <span>{t('rating.service_review')}</span>
                    </div>
                    <div style={styles.starsRow}>
                        {[1, 2, 3, 4, 5].map(star => (
                            <span key={star} style={{ ...styles.star, color: star <= rating ? 'var(--lc-main)' : 'var(--lc-surface-3, #e2e8f0)' }}>
                                <LcIcon name="star" size={26} />
                            </span>
                        ))}
                    </div>
                    <div style={styles.successMsg}>{t('rating.thank_you_alt')}</div>
                </div>
            );
        }

        return (
            <div style={styles.container}>
                <div style={styles.header}>
                    <LcIcon name="star" size={14} style={{ marginRight: '6px' }} />
                    <span>{this.props.prompt || t('rating.prompt_default')}</span>
                </div>
                <div
                    style={styles.starsRow}
                    onMouseLeave={this.handleStarLeave}
                >
                    {[1, 2, 3, 4, 5].map(star => (
                        <span
                            key={star}
                            style={{
                                ...styles.star,
                                color: star <= displayRating ? 'var(--lc-main)' : 'var(--lc-surface-3, #e2e8f0)',
                                transform: star <= displayRating ? 'scale(1.15)' : 'scale(1)',
                            }}
                            onClick={() => this.handleStarClick(star)}
                            onMouseEnter={() => this.handleStarHover(star)}
                        >
                            <LcIcon name="star" size={26} />
                        </span>
                    ))}
                </div>
                <textarea
                    style={styles.textarea}
                    placeholder={t('rating.feedback_placeholder')}
                    value={feedback}
                    onChange={e => this.setState({ feedback: e.target.value })}
                    maxLength={500}
                />
                {error && <div style={styles.errorMsg}>{error}</div>}
                <button
                    style={styles.button}
                    onClick={this.handleSubmit}
                    disabled={submitting || rating < 1}
                >
                    {submitting ? t('rating.submitting') : t('rating.submit_review')}
                </button>
            </div>
        );
    }
}
